import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Interview } from "@/types/recruitment";
import { Calendar, VideoCamera, MapPin, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export function InterviewScheduler() {
  const [interviews, setInterviews] = useKV<Interview[]>("recruitment-interviews", [
    {
      id: "int-1",
      applicationId: "app-1",
      jobId: "job-1",
      candidateId: "c1",
      interviewerId: "user-1",
      scheduledAt: new Date(Date.now() + 86400000).toISOString(),
      duration: 60,
      type: "Video",
      status: "Scheduled"
    }
  ]);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [newInterview, setNewInterview] = useState<Partial<Interview>>({ duration: 60, type: "Video" });

  const handleSchedule = () => {
    if (!newInterview.scheduledAt) {
      toast.error("Date required");
      return;
    }

    const interview: Interview = {
      id: crypto.randomUUID(),
      applicationId: "app-mock",
      jobId: "job-mock",
      candidateId: "candidate-mock",
      interviewerId: "CURRENT_USER",
      scheduledAt: newInterview.scheduledAt,
      duration: newInterview.duration || 60,
      type: newInterview.type as any,
      status: "Scheduled"
    };

    setInterviews([...interviews, interview]);
    setIsScheduleOpen(false);
    toast.success("Interview scheduled");
  };

  const updateStatus = (id: string, status: 'Completed' | 'Cancelled') => {
    setInterviews(interviews.map(i => i.id === id ? { ...i, status } : i));
    toast.success(`Interview marked as ${status}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Upcoming Interviews</h3>
        <Button onClick={() => setIsScheduleOpen(true)}>
          <Calendar className="mr-2 h-4 w-4" /> Schedule Interview
        </Button>
      </div>

      <div className="space-y-4">
        {interviews.map((interview) => (
          <Card key={interview.id} className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 flex justify-between items-center">
              <div className="flex items-start gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center min-w-[80px]">
                  <div className="text-xs text-blue-600 dark:text-blue-400 uppercase font-bold">
                    {new Date(interview.scheduledAt).toLocaleString('default', { month: 'short' })}
                  </div>
                  <div className="text-2xl font-bold">
                    {new Date(interview.scheduledAt).getDate()}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(interview.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold">Candidate Name (Mock)</h4>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                    <span className="flex items-center gap-1">
                      {interview.type === 'Video' ? <VideoCamera /> : <MapPin />} {interview.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock /> {interview.duration} mins
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge variant="outline">{interview.status}</Badge>
                {interview.status === 'Scheduled' && (
                  <>
                    <Button size="sm" variant="ghost" className="text-green-600" onClick={() => updateStatus(interview.id, 'Completed')}>
                      <CheckCircle className="w-5 h-5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => updateStatus(interview.id, 'Cancelled')}>
                      <XCircle className="w-5 h-5" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Interview</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Date & Time</Label>
              <Input 
                type="datetime-local" 
                onChange={(e) => setNewInterview({...newInterview, scheduledAt: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={newInterview.type} onValueChange={(v) => setNewInterview({...newInterview, type: v as any})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Video">Video Call</SelectItem>
                    <SelectItem value="Phone">Phone Call</SelectItem>
                    <SelectItem value="On-site">On-site</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration (mins)</Label>
                <Select value={String(newInterview.duration)} onValueChange={(v) => setNewInterview({...newInterview, duration: Number(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 mins</SelectItem>
                    <SelectItem value="45">45 mins</SelectItem>
                    <SelectItem value="60">60 mins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsScheduleOpen(false)}>Cancel</Button>
            <Button onClick={handleSchedule}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
